import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGraphQLClient,
  GET_BOOK_READER,
  SET_BOOK_AUTO_GENERATE,
  SAVE_BOOK_READING_POSITION,
  GENERATE_SCENE_PREVIEW,
  GENERATE_SCENE_PLAN,
  GENERATE_BOOK_PREVIEW,
  SUGGEST_SCENE_WORDING,
  UPDATE_SCENE_BODY,
  PUSH_BOOK_TO_GITHUB,
} from '../lib/graphql';

export interface BookReaderScene {
  id: string;
  title: string;
  position: number;
  body: string | null;
  previewContent: string | null;
  health: string | null;
  wordCount: number | null;
  planPoints: { id: string; content: string }[];
}

export interface BookReaderChapter {
  id: string;
  title: string;
  position: number;
  scenes: BookReaderScene[];
}

export interface BookReaderData {
  id: string;
  title: string;
  fullTitle: string;
  autoGenerate: boolean;
  githubSyncConfigured: boolean;
  readingPosition: { blockId: string; charOffset: number } | null;
  chapters: BookReaderChapter[];
}

// One-button GitHub sync: pushBookToGithub already pulls + reconciles + commits.
// Returns whether the sync was held back by an unresolved conflict so the reader
// can route to the full sync page for resolution.
export interface SyncResult {
  conflicts: boolean;
  errors: string[];
}

async function fetchBookReader(id: string): Promise<BookReaderData | null> {
  const data = await getGraphQLClient().request<{ book: BookReaderData | null }>(GET_BOOK_READER, {
    id,
  });
  return data.book;
}

export function useBookReader(bookId: string | undefined) {
  return useQuery({
    queryKey: ['bookReader', bookId],
    queryFn: () => fetchBookReader(bookId as string),
    enabled: !!bookId,
  });
}

export function useSetBookAutoGenerate(bookId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      getGraphQLClient().request(SET_BOOK_AUTO_GENERATE, { bookId, enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookReader', bookId] }),
  });
}

// Generates a scene's quick preview (synchronous-ish); the reader refetches so
// the new prose appears. Used both manually and by the autogenerate loop.
export function useGenerateScenePreview(bookId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sceneId: string) =>
      getGraphQLClient().request(GENERATE_SCENE_PREVIEW, { sceneId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookReader', bookId] }),
  });
}

// Generates and persists a scene's plan (scenos planas) — the key moments prose
// is drafted from. Used by the frontier loop before prose, and manually.
export function useGenerateScenePlan(bookId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sceneId: string) => getGraphQLClient().request(GENERATE_SCENE_PLAN, { sceneId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookReader', bookId] }),
  });
}

// Generates previews for every scene in the book in order (synchronous backend).
export function useGenerateBookPreview(bookId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => getGraphQLClient().request(GENERATE_BOOK_PREVIEW, { bookId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookReader', bookId] }),
  });
}

// Syncs the book with its GitHub repo (pull + reconcile + commit in one call).
// The reader refetches so any pulled prose appears.
export function useSyncBook(bookId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<SyncResult> => {
      const data = await getGraphQLClient().request<{
        pushBookToGithub: { conflicts: boolean; errors: string[] };
      }>(PUSH_BOOK_TO_GITHUB, { bookId, message: null });
      return data.pushBookToGithub;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookReader', bookId] }),
  });
}

export function useSaveBookReadingPosition() {
  return useMutation({
    mutationFn: (input: { bookId: string; blockId: string; charOffset?: number }) =>
      getGraphQLClient().request(SAVE_BOOK_READING_POSITION, input),
  });
}

// AI drop-in alternatives for a phrase the reader selected in scene prose.
// Synchronous; makes no changes — applying a choice goes through useUpdateSceneBody.
export function useSuggestSceneWording() {
  return useMutation({
    mutationFn: async (input: {
      sceneId: string;
      selectedText: string;
      precedingText?: string;
      followingText?: string;
    }): Promise<string[]> => {
      const data = await getGraphQLClient().request<{
        suggestSceneWording: { suggestions: string[]; errors: string[] };
      }>(SUGGEST_SCENE_WORDING, input);
      return data.suggestSceneWording.suggestions;
    },
  });
}

// Saves a scene's full body (after splicing a reworded phrase back in); the
// reader refetches so the new prose appears.
export function useUpdateSceneBody(bookId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sceneId: string; content: string }) =>
      getGraphQLClient().request(UPDATE_SCENE_BODY, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookReader', bookId] }),
  });
}
